import { useSnackbar } from '@app/hooks/useSnackbar';
import {
  KlausOrgOption,
  useKlausLinkableOrgsQuery,
  useLinkCompanyToKlausMutation,
} from '@dieterApi/company/useKlausLinkMutations';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useEffect, useState } from 'react';

interface Props {
  company: { id: string; name: string } | null;
  onClose: () => void;
}

/**
 * Verknüpft einen Mandanten mit einer Klaus-Organisation. Zur Auswahl stehen
 * die Klaus-Organisationen, die Klaus dem Admin als unverknüpft meldet.
 * Danach übernimmt Klaus die Mitglieder des Mandanten aus Dieter.
 */
export function KlausLinkDialog({ company, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [loadOrgs, { data, loading }] = useKlausLinkableOrgsQuery();
  const [link, { loading: linking }] = useLinkCompanyToKlausMutation();
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (company) {
      setSelected('');
      loadOrgs();
    }
  }, [company, loadOrgs]);

  const orgs: KlausOrgOption[] = data?.klausLinkableOrgs ?? [];
  // Gleichnamige Klaus-Organisation vorschlagen.
  useEffect(() => {
    if (!company || selected) return;
    const match = orgs.find((o) => o.name.trim().toLowerCase() === company.name.trim().toLowerCase());
    if (match) setSelected(match.id);
  }, [orgs, company, selected]);

  const handleLink = () => {
    if (!company || !selected) return;
    link({ variables: { companyId: company.id, klausOrgId: selected } })
      .then(() => {
        enqueueSnackbar('Mandant mit Klaus verknüpft. Klaus übernimmt die Mitglieder aus Dieter.', { variant: 'success' });
        onClose();
      })
      .catch((e) => enqueueSnackbar(e.message, { variant: 'error', stack: e.stack }));
  };

  return (
    <Dialog open={Boolean(company)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mit Klaus verknüpfen</DialogTitle>
      <DialogContent>
        <p className="mb-4 text-sm">
          Wähle die Klaus-Organisation für <b>{company?.name}</b>. Danach gelten in Klaus die Mitglieder und Rollen
          dieses Mandanten aus Dieter.
        </p>
        {loading ? (
          <p className="text-sm">Klaus-Organisationen werden geladen …</p>
        ) : orgs.length === 0 ? (
          <p className="text-sm">
            Keine freie Klaus-Organisation gefunden. In Klaus legt die Kanzlei Mandanten unter „Verwaltung“ an, indem sie
            sie aus Dieter übernimmt; dabei wird auch gleich verknüpft.
          </p>
        ) : (
          <FormControl fullWidth size="small">
            <InputLabel id="klaus-org-label">Klaus-Organisation</InputLabel>
            <Select
              labelId="klaus-org-label"
              label="Klaus-Organisation"
              value={selected}
              onChange={(e) => setSelected(String(e.target.value))}
            >
              {orgs.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={handleLink} disabled={!selected || linking}>
          Verknüpfen
        </Button>
      </DialogActions>
    </Dialog>
  );
}
